import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Eye, EyeOff, Edit, Trash2, LogOut, Bell, Send, CheckCircle, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslations } from '../translations';
import { toast } from 'sonner';
import { apiClient } from '../lib/apiClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlueZoneLayouts = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useTranslations(language);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [enrichedDetail, setEnrichedDetail] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteShowPw, setDeleteShowPw] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Alan Onayı & Sözleşme sekmesi
  const [activeMainTab, setActiveMainTab] = useState('all');
  const [approvedParticipants, setApprovedParticipants] = useState([]);
  const [approvedLoading, setApprovedLoading] = useState(false);
  // Sözleşme gönderme modal
  const [contractModal, setContractModal] = useState(null); // participant obj
  const [contractPrice, setContractPrice] = useState('');
  const [contractCurrency, setContractCurrency] = useState('USD');
  const [sendingContract, setSendingContract] = useState(false);

  // Helper: Kenar segment'lerini hesapla
  const getEdgeSegments = (selectedCells, baseW, baseH, side) => {
    if (!selectedCells || !selectedCells.length) return [];
    
    const segments = [];
    
    if (side === 'north') {
      for (let x = 0; x < Math.max(baseW, 30); x++) {
        const colCells = selectedCells.filter(([cx]) => cx === x);
        if (colCells.length > 0) {
          const minY = Math.min(...colCells.map(([, cy]) => cy));
          segments.push({ x1: x, y1: minY, x2: x + 1, y2: minY });
        }
      }
    } else if (side === 'south') {
      for (let x = 0; x < Math.max(baseW, 30); x++) {
        const colCells = selectedCells.filter(([cx]) => cx === x);
        if (colCells.length > 0) {
          const maxY = Math.max(...colCells.map(([, cy]) => cy));
          segments.push({ x1: x, y1: maxY + 1, x2: x + 1, y2: maxY + 1 });
        }
      }
    } else if (side === 'east') {
      for (let y = 0; y < Math.max(baseH, 25); y++) {
        const rowCells = selectedCells.filter(([, cy]) => cy === y);
        if (rowCells.length > 0) {
          const maxX = Math.max(...rowCells.map(([cx]) => cx));
          segments.push({ x1: maxX + 1, y1: y, x2: maxX + 1, y2: y + 1 });
        }
      }
    } else if (side === 'west') {
      for (let y = 0; y < Math.max(baseH, 25); y++) {
        const rowCells = selectedCells.filter(([, cy]) => cy === y);
        if (rowCells.length > 0) {
          const minX = Math.min(...rowCells.map(([cx]) => cx));
          segments.push({ x1: minX, y1: y, x2: minX, y2: y + 1 });
        }
      }
    }
    
    return segments;
  };

  // Segment color
  const getSegmentColor = (selectedCells, baseW, baseH, openSides, side, x, y) => {
    let checkX = x, checkY = y;
    if (side === 'north') checkY = y;
    else if (side === 'south') checkY = y - 1;
    else if (side === 'east') checkX = x - 1;
    else if (side === 'west') checkX = x;
    
    const isSelected = selectedCells.some(([cx, cy]) => cx === checkX && cy === checkY);
    const isExtrusion = isSelected && (checkX >= baseW || checkY >= baseH);
    
    if (isExtrusion) return '#22c55e';
    return openSides.includes(side) ? '#22c55e' : '#ef4444';
  };

  useEffect(() => {
    fetchParticipants();
  }, []);

  const fetchParticipants = async () => {
    const token = localStorage.getItem('cop31_ultra_token');
    try {
      const response = await apiClient.get(`/ultra-admin/blue-zone-layouts`);
      setParticipants(response.data);
    } catch (error) {
      console.error('Failed to fetch participants');
    } finally {
      setLoading(false);
    }
  };

  const fetchApproved = async () => {
    const token = localStorage.getItem('cop31_ultra_token');
    setApprovedLoading(true);
    try {
      const res = await apiClient.get(`/ultra-admin/blue-zone-layouts-approved`);
      setApprovedParticipants(res.data || []);
    } catch { /* ignore */ }
    finally { setApprovedLoading(false); }
  };

  const openContractModal = (participant) => {
    setContractModal(participant);
    // Hall'ın default_price_per_sqm'sini otomatik çek
    const hallId = participant.hall_stand_id || null;
    // Varsayılan fiyatı doldur — Hall'a sahipse default fiyatı çek
    setContractPrice(participant.default_price_per_sqm || '');
    setContractCurrency(participant.contract_currency || 'USD');
    // Eğer hall adı varsa, hall'ın fiyatını çekmeyi dene
    if (participant.hall) {
      const token = localStorage.getItem('cop31_ultra_token');
      apiClient.get(`/ultra-admin/halls`)
        .then(r => {
          const halls = r.data || [];
          const matchedHall = halls.find(h => h.name === participant.hall || h.id === participant.hall_id);
          if (matchedHall && matchedHall.default_price_per_sqm) {
            setContractPrice(String(matchedHall.default_price_per_sqm));
            setContractCurrency(matchedHall.default_currency || 'USD');
          }
        })
        .catch(() => {});
    }
  };

  const handleSendContract = async () => {
    if (!contractModal) return;
    const price = parseFloat(contractPrice);
    if (!price || price <= 0) {
      toast.error(language === 'tr' ? 'Lütfen geçerli bir m² fiyatı girin.' : 'Please enter a valid price per m².');
      return;
    }
    setSendingContract(true);
    const token = localStorage.getItem('cop31_ultra_token');
    try {
      const res = await apiClient.post(
        `/ultra-admin/blue-zone-layouts/${contractModal.id}/send-contract`,
        { price_per_sqm: price, currency: contractCurrency }
      );
      const totalFmt = Number(res.data.contract_total_amount || 0).toLocaleString('tr-TR');
      toast.success(language === 'tr'
        ? `Sözleşme gönderildi! Toplam Tutar: ${contractCurrency} ${totalFmt}`
        : `Contract sent! Total: ${contractCurrency} ${totalFmt}`);
      setContractModal(null);
      fetchApproved();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'tr' ? 'Gönderme başarısız.' : 'Send failed.'));
    } finally { setSendingContract(false); }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/giris');
  };

  const handleTabChange = (tab) => {
    setActiveMainTab(tab);
    if (tab === 'approved') fetchApproved();
  };

  const handleView = async (participant) => {
    setSelectedParticipant(participant);
    setEnrichedDetail(null);
    setShowPreview(true);
    // Fetch enriched details (tenant + main user) in the background
    try {
      const token = localStorage.getItem('cop31_ultra_token') || localStorage.getItem('cop31_token');
      const r = await apiClient.get(`/ultra-admin/blue-zone-layouts/${participant.id}/enriched`);
      setEnrichedDetail(r.data);
    } catch (e) {
      // silent — modal still works with participant data alone
    }
  };

  const handleEdit = (participantId) => {
    navigate(`/ultra-admin/blue-zone/edit/${participantId}`);
  };

  const handleDeleteClick = (participantId) => {
    setDeleteId(participantId);
    setDeletePassword('');
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    if (!deletePassword) {
      setDeleteError(language === 'tr' ? 'Şifre gerekli!' : 'Password required!');
      return;
    }

    const token = localStorage.getItem('cop31_ultra_token');
    try {
      await apiClient.delete(
        `/ultra-admin/blue-zone-layouts/${deleteId}?ultra_password=${encodeURIComponent(deletePassword)}`
      );
      setDeleteId(null);
      setDeletePassword('');
      fetchParticipants();
    } catch (error) {
      if (error.response?.status === 403) {
        setDeleteError(language === 'tr' ? 'Ultra Password hatalı!' : 'Wrong Ultra Password!');
      } else {
        setDeleteError(language === 'tr' ? 'Silme başarısız' : 'Delete failed');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      
      <div className="flex-1">
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t('blue_zone_layouts')}</h1>
              </div>
              <div className="flex items-center gap-4">
                <LanguageToggle variant="admin" />
                <Button onClick={handleLogout} variant="outline" className="rounded-full">
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('logout')}
                </Button>
              </div>
            </div>
          </div>
        </nav>

        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <PageIntro {...getPageIntro('blue-zone', language)} />
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{t('all_participants')}</h2>
            <Button onClick={() => navigate('/ultra-admin/blue-zone/new')} className="bg-purple-700 hover:bg-purple-800 rounded-full">
              <Plus className="h-4 w-4 mr-2" />
              {t('add_participant')}
            </Button>
          </div>

          <Tabs value={activeMainTab} onValueChange={handleTabChange} className="space-y-6">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="all" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                {language === 'tr' ? 'Tüm Katılımcılar' : 'All Participants'}
                <span className="bg-purple-100 text-purple-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{participants.length}</span>
              </TabsTrigger>
              <TabsTrigger value="approved" className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                {language === 'tr' ? 'Onaylananlar' : 'Approved'}
                {approvedParticipants.length > 0 && (
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{approvedParticipants.length}</span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* ── TÜM KATILIMCILAR ── */}
            <TabsContent value="all">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-12 w-12 border-4 border-purple-700 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
            </div>
          ) : participants.length === 0 ? (
            <Card className="border-0 shadow-lg rounded-3xl">
              <CardContent className="py-16 text-center">
                <MapPin className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600 mb-4">{language === 'tr' ? 'Henüz katılımcı eklenmemiş' : 'No participants added yet'}</p>
                <Button onClick={() => navigate('/ultra-admin/blue-zone/new')} className="bg-purple-700 rounded-full">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('add_participant')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {participants.map((participant) => (
                <Card key={participant.id} className="border-0 shadow-lg rounded-3xl">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        {/* Phase 86Y — Country becomes the primary heading,
                            email moves to a smaller secondary line. The old
                            order (tiny country pill + huge email) buried the
                            most important identity signal on this list. */}
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <h3 className="font-bold text-lg text-gray-900">{participant.country || (language === 'tr' ? 'Ülke yok' : 'No country')}</h3>
                          {participant.stand_no && <Badge className="bg-purple-100 text-purple-700 rounded-full">Stand: {participant.stand_no}</Badge>}
                          {participant.hall && <Badge className="bg-orange-100 text-orange-700 rounded-full">{participant.hall}</Badge>}
                          {/* Lifecycle status pill — mirrors Stand Requests page */}
                          {(() => {
                            const hasChange  = (participant.pending_change_count || 0) > 0;
                            const isApproved = participant.area_approval_status === 'approved';
                            if (hasChange) return (
                              <Badge className="bg-amber-500 text-white rounded-full flex items-center gap-1 animate-pulse">
                                {language === 'tr' ? 'Değişiklik İstendi' : 'Change Requested'}
                              </Badge>
                            );
                            if (isApproved) return (
                              <Badge className="bg-green-600 text-white rounded-full flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" /> {language === 'tr' ? 'Müşteri Onayladı' : 'Customer Approved'}
                              </Badge>
                            );
                            return (
                              <Badge className="bg-orange-100 text-orange-700 border border-orange-300 rounded-full">
                                {language === 'tr' ? 'Onay Bekliyor' : 'Awaiting Approval'}
                              </Badge>
                            );
                          })()}
                          {participant.contract_status === 'signed' && (
                            <Badge className="bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
                              <FileText className="h-3 w-3" /> {language === 'tr' ? 'Sözleşme İmzalandı' : 'Contract Signed'}
                            </Badge>
                          )}
                          {participant.contract_status === 'sent' && (
                            <Badge className="bg-sky-100 text-sky-700 rounded-full flex items-center gap-1">
                              <Send className="h-3 w-3" /> {language === 'tr' ? 'Sözleşme Gönderildi' : 'Contract Sent'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mb-3 truncate" data-testid={`participant-email-${participant.id}`}>
                          {participant.main_user_email || participant.email}
                        </p>
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                          <div>
                            <p className="font-semibold mb-1">{language === 'tr' ? 'Stand Boyutları:' : 'Stand Dimensions:'}</p>
                            <p>{participant.layout ? `${participant.layout.base_width}m x ${participant.layout.base_height}m = ${participant.layout.total_area}m²` : (language === 'tr' ? 'Layout bekleniyor' : 'Layout pending')}</p>
                          </div>
                          <div>
                            <p className="font-semibold mb-1">
                              {language === 'tr' ? 'Yetkili Kişiler:' : 'Authorized Persons:'}{' '}
                              <span className="text-gray-400 font-normal">({participant.person_count || 0})</span>
                            </p>
                            {(() => {
                              const names = [];
                              if (participant.main_user_name) names.push(participant.main_user_name);
                              (participant.authorized_person_names || []).forEach(n => { if (n) names.push(n); });
                              if (names.length === 0) {
                                return <p className="text-gray-400 italic">{language === 'tr' ? 'Henüz kayıtlı kişi yok' : 'No authorized persons yet'}</p>;
                              }
                              return <p className="break-words">{names.join(', ')}</p>;
                            })()}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleView(participant)} variant="ghost" size="sm" className="rounded-full hover:bg-sky-100">
                          <Eye className="h-4 w-4 text-sky-600" />
                        </Button>
                        <Button onClick={() => handleEdit(participant.id)} variant="ghost" size="sm" className="rounded-full hover:bg-purple-100">
                          <Edit className="h-4 w-4 text-purple-700" />
                        </Button>
                        <Button onClick={() => handleDeleteClick(participant.id)} variant="ghost" size="sm" className="rounded-full hover:bg-red-100">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
            </TabsContent>

            {/* ── ONAYLANANLAR (Alan Onayı + Sözleşme) ── */}
            <TabsContent value="approved">
              {approvedLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-gray-500 text-sm">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
                </div>
              ) : approvedParticipants.length === 0 ? (
                <Card className="border-0 shadow-lg rounded-3xl">
                  <CardContent className="py-16 text-center">
                    <CheckCircle className="h-14 w-14 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500 text-sm">{language === 'tr' ? 'Henüz alan onayı veren katılımcı yok.' : 'No participants have approved their area yet.'}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {approvedParticipants.map((p) => {
                    const totalArea = p.layout?.total_area || 0;
                    const contractStatus = p.contract_status;
                    return (
                      <Card key={p.id} className="border-0 shadow-lg rounded-3xl overflow-hidden">
                        <div className="h-1 w-full" style={{background: contractStatus === 'signed' ? '#22c55e' : contractStatus === 'sent' ? '#3b82f6' : '#f59e0b'}} />
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                <h3 className="font-bold text-gray-900">{p.full_name || p.email}</h3>
                                <Badge className="rounded-full text-xs">{p.country}</Badge>
                                {p.stand_no && <Badge className="bg-purple-100 text-purple-700 rounded-full text-xs">Stand: {p.stand_no}</Badge>}
                                {p.hall && <Badge className="bg-orange-100 text-orange-700 rounded-full text-xs">{p.hall}</Badge>}
                                <Badge className={
                                  contractStatus === 'signed' ? 'bg-emerald-100 text-emerald-700 rounded-full text-xs' :
                                  contractStatus === 'sent' ? 'bg-blue-100 text-blue-700 rounded-full text-xs' :
                                  'bg-amber-100 text-amber-700 rounded-full text-xs'
                                }>
                                  {contractStatus === 'signed'
                                    ? (language === 'tr' ? '✅ Sözleşme İmzalandı' : '✅ Contract Signed')
                                    : contractStatus === 'sent'
                                      ? (language === 'tr' ? '📨 Sözleşme Gönderildi' : '📨 Contract Sent')
                                      : (language === 'tr' ? '✓ Alan Onaylandı' : '✓ Area Approved')}
                                </Badge>
                              </div>
                              <div className="text-sm text-gray-500 space-y-0.5">
                                <p>{p.email}</p>
                                {p.company && <p className="font-medium text-gray-700">{p.company}</p>}
                                <p>{language === 'tr' ? 'Stand Alanı:' : 'Area:'} <strong className="text-gray-800">{totalArea} m²</strong></p>
                                {p.area_approved_at && (
                                  <p className="text-xs text-gray-400">
                                    {language === 'tr' ? 'Onay tarihi:' : 'Approved:'} {new Date(p.area_approved_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US')}
                                  </p>
                                )}
                                {contractStatus === 'signed' && p.contract_signer_name && (
                                  <p className="text-xs text-emerald-600 font-semibold">
                                    {language === 'tr' ? 'İmzalayan:' : 'Signed by:'} {p.contract_signer_name}
                                  </p>
                                )}
                                {contractStatus === 'signed' && p.contract_total_amount && (
                                  <p className="text-xs text-gray-600">
                                    {language === 'tr' ? 'Tutar:' : 'Amount:'} <strong>{p.contract_currency || 'USD'} {Number(p.contract_total_amount).toLocaleString()}</strong>
                                  </p>
                                )}
                              </div>
                            </div>
                            {/* Sözleşme Aksiyon */}
                            <div className="flex-shrink-0">
                              {contractStatus === 'signed' ? (
                                <div className="flex items-center gap-1 text-emerald-600 text-sm font-semibold">
                                  <CheckCircle className="h-5 w-5" />
                                  {language === 'tr' ? 'Tamamlandı' : 'Completed'}
                                </div>
                              ) : contractStatus === 'sent' ? (
                                <div className="flex flex-col items-end gap-2">
                                  <span className="text-xs text-blue-500 font-medium">{language === 'tr' ? 'İmza bekleniyor...' : 'Awaiting signature...'}</span>
                                  <Button
                                    onClick={() => openContractModal(p)}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs rounded-full border-blue-200 text-blue-600 hover:bg-blue-50"
                                  >
                                    <Send className="h-3 w-3 mr-1" /> {language === 'tr' ? 'Tekrar Gönder' : 'Resend'}
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  onClick={() => openContractModal(p)}
                                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                                  data-testid={`send-contract-btn-${p.id}`}
                                >
                                  <FileText className="h-4 w-4 mr-1.5" />
                                  {language === 'tr' ? 'Sözleşme Gönder' : 'Send Contract'}
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* ── Sözleşme Gönderme Modal ── */}
      {contractModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setContractModal(null)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-black text-gray-900">{language === 'tr' ? 'Sözleşme Gönder' : 'Send Contract'}</h2>
                <p className="text-sm text-gray-500 mt-0.5">{contractModal.full_name || contractModal.email}</p>
              </div>
              <button onClick={() => setContractModal(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            {/* Summary */}
            <div className="bg-blue-50 rounded-2xl p-4 mb-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-gray-400 mb-0.5">{language === 'tr' ? 'Stand No' : 'Stand No'}</p><p className="font-bold text-blue-700">{contractModal.stand_no || '—'}</p></div>
                <div><p className="text-xs text-gray-400 mb-0.5">Hall</p><p className="font-semibold text-gray-700">{contractModal.hall || '—'}</p></div>
                <div><p className="text-xs text-gray-400 mb-0.5">{language === 'tr' ? 'Stand Alanı' : 'Stand Area'}</p><p className="font-semibold text-gray-700">{contractModal.layout?.total_area || 0} m²</p></div>
                <div><p className="text-xs text-gray-400 mb-0.5">Email</p><p className="font-semibold text-gray-700 text-xs truncate">{contractModal.email}</p></div>
              </div>
            </div>

            <div className="space-y-4 mb-5">
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-1.5 block">
                  {language === 'tr' ? 'm² Birim Fiyatı' : 'Price per m²'}
                  <span className="text-xs text-gray-400 font-normal ml-2">{language === 'tr' ? '(Otomatik çekilir, değiştirilebilir)' : '(Auto-pulled, editable)'}</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={contractPrice}
                    onChange={e => setContractPrice(e.target.value)}
                    placeholder="500"
                    className="flex-1 rounded-xl"
                    data-testid="contract-price-input"
                  />
                  <select
                    value={contractCurrency}
                    onChange={e => setContractCurrency(e.target.value)}
                    className="w-24 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    data-testid="contract-currency-select"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="TRY">TRY</option>
                  </select>
                </div>
              </div>

              {contractPrice && parseFloat(contractPrice) > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{language === 'tr' ? 'Toplam Tutar' : 'Total Amount'}</span>
                    <span className="text-lg font-black text-emerald-700">
                      {contractCurrency} {(parseFloat(contractPrice || 0) * (contractModal.layout?.total_area || 0)).toLocaleString('tr-TR')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{parseFloat(contractPrice || 0)} × {contractModal.layout?.total_area || 0} m²</p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setContractModal(null)}
                className="flex-1 border border-gray-200 rounded-xl py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {language === 'tr' ? 'İptal' : 'Cancel'}
              </button>
              <button
                onClick={handleSendContract}
                disabled={sendingContract || !contractPrice || parseFloat(contractPrice) <= 0}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-3 text-sm font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-send-contract-btn"
              >
                {sendingContract ? (
                  <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {language === 'tr' ? 'Gönderiliyor...' : 'Sending...'}</>
                ) : (
                  <><Send className="h-4 w-4" /> {language === 'tr' ? 'Sözleşmeyi Gönder' : 'Send Contract'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && selectedParticipant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPreview(false)}>
          <Card className="border-0 shadow-2xl rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-2xl">{language === 'tr' ? 'Layout Detaylar\u0131' : 'Layout Details'}</CardTitle>
                <Button onClick={() => setShowPreview(false)} variant="ghost" size="sm" className="text-2xl hover:bg-gray-100 rounded-full w-10 h-10">✕</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-8">
                {/* Left Side - Info */}
                <div className="space-y-6">
                  {/* Country / Representative card — prominent */}
                  <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg">
                    <p className="text-[11px] font-bold tracking-widest text-blue-100 uppercase mb-1">
                      {language === 'tr' ? 'Ülke / Tenant' : 'Country / Tenant'}
                    </p>
                    <h2 className="text-3xl font-extrabold mb-3 leading-tight" data-testid="layout-detail-country">
                      {(enrichedDetail && enrichedDetail.country) || selectedParticipant.country || '—'}
                    </h2>
                    {/* Representative person */}
                    {(enrichedDetail && enrichedDetail.main_user) ? (
                      <>
                        <p className="text-base font-bold text-white mb-1">
                          {enrichedDetail.main_user.full_name || (language === 'tr' ? 'Ana Kullanıcı' : 'Main User')}
                        </p>
                        <div className="text-sm text-blue-100 space-y-0.5">
                          <p>📧 {enrichedDetail.main_user.email}</p>
                          {(enrichedDetail.main_user.phone || enrichedDetail.phone) && (
                            <p>📞 {enrichedDetail.main_user.phone || enrichedDetail.phone}</p>
                          )}
                          <p>🏢 {language === 'tr' ? 'Mavi Bölge' : 'Blue Zone'}</p>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-blue-100">{selectedParticipant.email}</p>
                    )}
                  </div>

                  {/* Stand info card */}
                  <div className="bg-purple-50 rounded-2xl p-6">
                    <h3 className="font-bold text-lg mb-4">{language === 'tr' ? 'Stand Bilgileri' : 'Stand Information'}</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {selectedParticipant.stand_no && (
                        <div className="bg-white rounded-xl p-3 border border-purple-100">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{language === 'tr' ? 'Stand No' : 'Stand No'}</p>
                          <p className="font-bold text-purple-700 text-lg">{selectedParticipant.stand_no}</p>
                        </div>
                      )}
                      {selectedParticipant.hall && (
                        <div className="bg-white rounded-xl p-3 border border-purple-100">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{language === 'tr' ? 'Hall' : 'Hall'}</p>
                          <p className="font-bold text-gray-800 text-sm">{selectedParticipant.hall}</p>
                        </div>
                      )}
                      {enrichedDetail?.stand_design_type && (
                        <div className="bg-white rounded-xl p-3 border border-purple-100">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{language === 'tr' ? 'Tasarım' : 'Design'}</p>
                          <p className="font-bold text-gray-800 text-sm capitalize">
                            {enrichedDetail.stand_design_type === 'modular' ? (language === 'tr' ? 'Modüler' : 'Modular') : (language === 'tr' ? 'Özel Tasarım' : 'Custom Design')}
                          </p>
                        </div>
                      )}
                      {enrichedDetail?.stand_layout_type && (
                        <div className="bg-white rounded-xl p-3 border border-purple-100">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{language === 'tr' ? 'Stand Tipi' : 'Layout'}</p>
                          <p className="font-bold text-gray-800 text-sm capitalize">{enrichedDetail.stand_layout_type}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-sky-50 rounded-2xl p-6">
                    <h3 className="font-bold text-lg mb-4">{language === 'tr' ? 'Stand Ölçüleri' : 'Stand Dimensions'}</h3>
                    {selectedParticipant.layout ? (
                    <p className="text-3xl font-bold text-purple-700 mb-3">{selectedParticipant.layout.base_width}m x {selectedParticipant.layout.base_height}m</p>
                    ) : <p className="text-xl text-gray-400 mb-3">{language === 'tr' ? 'Layout henüz atanmadı' : 'Layout not assigned yet'}</p>}
                    {selectedParticipant.layout && <p className="text-xl font-semibold text-sky-700">{language === 'tr' ? 'Toplam Alan:' : 'Total Area:'} {selectedParticipant.layout.total_area}m²</p>}
                    {selectedParticipant.layout && <div className="mt-4">
                      <p className="font-semibold mb-2 text-sm">{language === 'tr' ? 'Açık Kenarlar:' : 'Open Sides:'}</p>
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          // Map north/east/south/west → A/B/C/D segment labels
                          // Hall management uses A=top, B=right, C=bottom, D=left
                          const sideToSegment = { north: 'A', east: 'B', south: 'C', west: 'D' };
                          const colorMap = { A: 'bg-red-500', B: 'bg-orange-500', C: 'bg-purple-500', D: 'bg-emerald-500' };
                          const opens = (selectedParticipant.layout.open_sides || []).map(s =>
                            // Already an A/B/C/D segment? keep it. Else map.
                            (s && /^[A-D]$/.test(s)) ? s : sideToSegment[String(s).toLowerCase()] || s
                          );
                          // Dedupe + sort A→D
                          const set = Array.from(new Set(opens)).sort();
                          if (!set.length) return <span className="text-xs text-gray-400">{language === 'tr' ? '(yok)' : '(none)'}</span>;
                          return set.map(seg => (
                            <span key={seg} className={`text-white text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1.5 ${colorMap[seg] || 'bg-gray-500'}`}>
                              <span className="h-5 w-5 rounded-full bg-white/30 flex items-center justify-center text-[11px]">{seg}</span>
                              {language === 'tr' ? 'Açık' : 'Open'}
                            </span>
                          ));
                        })()}
                      </div>
                    </div>}
                  </div>

                  <div className="bg-purple-50 rounded-2xl p-6">
                    <h3 className="font-bold text-lg mb-4">{language === 'tr' ? 'Yetkili Kişiler' : 'Authorized Persons'} ({(enrichedDetail?.main_user ? 1 : 0) + (selectedParticipant.authorized_persons || []).length})</h3>
                    <div className="space-y-3">
                      {/* Always show the main customer-admin user as the primary representative */}
                      {enrichedDetail && enrichedDetail.main_user && (
                        <div className="bg-white rounded-xl p-4 border-2 border-blue-300 relative">
                          <span className="absolute -top-2 left-3 text-[9px] font-bold bg-blue-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">
                            {language === 'tr' ? 'Ana Temsilci' : 'Primary Contact'}
                          </span>
                          <p className="font-bold text-gray-900 mb-1 mt-1">{enrichedDetail.main_user.full_name || '—'}</p>
                          <div className="grid grid-cols-1 gap-1 text-xs text-gray-600">
                            <p><span className="font-medium">Email:</span> {enrichedDetail.main_user.email}</p>
                            {enrichedDetail.main_user.phone && (
                              <p><span className="font-medium">{language === 'tr' ? 'Telefon:' : 'Phone:'}</span> {enrichedDetail.main_user.phone}</p>
                            )}
                            <p><span className="font-medium">{language === 'tr' ? 'Rol:' : 'Role:'}</span> {language === 'tr' ? 'Müşteri Yöneticisi' : 'Customer Admin'}</p>
                          </div>
                        </div>
                      )}
                      {(selectedParticipant.authorized_persons || []).map((person, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-4 border border-purple-200">
                          <p className="font-semibold text-gray-900 mb-2">{person.name}</p>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                            <p><span className="font-medium">{language === 'tr' ? 'Mobil:' : 'Mobile:'}</span> {person.mobile}</p>
                            {person.phone && <p><span className="font-medium">{language === 'tr' ? 'Tel:' : 'Phone:'}</span> {person.phone}</p>}
                            <p><span className="font-medium">Email:</span> {person.email}</p>
                            <p><span className="font-medium">{language === 'tr' ? 'Unvan:' : 'Title:'}</span> {person.title}</p>
                          </div>
                        </div>
                      ))}
                      {!enrichedDetail?.main_user && (selectedParticipant.authorized_persons || []).length === 0 && (
                        <p className="text-sm text-gray-400 italic">{language === 'tr' ? 'Henüz kayıtlı kişi yok.' : 'No authorized persons yet.'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Side - Layout Visual */}
                <div>
                  <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 sticky top-4">
                    <h3 className="font-bold text-lg mb-6 text-center">{language === 'tr' ? 'Stand G\u00f6r\u00fcn\u00fcm\u00fc' : 'Stand Visualization'}</h3>
                    <div className="flex items-center justify-center min-h-96 bg-gray-50 rounded-xl p-4">
                      {!selectedParticipant.layout ? (
                        <div className="text-center text-gray-400">
                          <p className="text-lg font-semibold">{language === 'tr' ? 'Layout Bekleniyor' : 'Layout Pending'}</p>
                          <p className="text-sm mt-1">{language === 'tr' ? 'Stand düzeni henüz atanmadı' : 'Stand layout not assigned yet'}</p>
                        </div>
                      ) : (() => {
                        const cells = selectedParticipant.layout.grid_cells || [];
                        const w = selectedParticipant.layout.base_width;
                        const h = selectedParticipant.layout.base_height;
                        const cellSize = 32;          // bigger cells, easier to read
                        const padding  = 50;
                        const svgW = w * cellSize + padding * 2;
                        const svgH = h * cellSize + padding * 2;

                        // Hall-management palette: amber/orange = reserved
                        const RESERVED_FILL   = '#fb923c';   // orange-400
                        const RESERVED_STROKE = '#c2410c';   // orange-700
                        const GRID_STROKE     = '#fed7aa';   // orange-200 (cell lines)

                        return (
                          <svg width={svgW} height={svgH}>
                            {/* per-cell rectangles with visible 1m grid lines */}
                            {cells.map(([x, y]) => (
                              <rect
                                key={`cell-${x}-${y}`}
                                x={padding + x * cellSize}
                                y={padding + y * cellSize}
                                width={cellSize}
                                height={cellSize}
                                fill={RESERVED_FILL}
                                stroke={GRID_STROKE}
                                strokeWidth="1"
                              />
                            ))}
                            {/* Outer stand border */}
                            <rect
                              x={padding}
                              y={padding}
                              width={w * cellSize}
                              height={h * cellSize}
                              fill="none"
                              stroke={RESERVED_STROKE}
                              strokeWidth="2.5"
                            />
                            {/* Stand No label centered */}
                            <text
                              x={padding + (w * cellSize) / 2}
                              y={padding + (h * cellSize) / 2 - 8}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fill="white"
                              fontSize={Math.min(28, w * cellSize * 0.18)}
                              fontWeight="800"
                              style={{ paintOrder: 'stroke', stroke: RESERVED_STROKE, strokeWidth: 3 }}
                            >
                              {selectedParticipant.stand_no}
                            </text>
                            <text
                              x={padding + (w * cellSize) / 2}
                              y={padding + (h * cellSize) / 2 + 16}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fill="white"
                              fontSize="13"
                              fontWeight="700"
                              style={{ paintOrder: 'stroke', stroke: RESERVED_STROKE, strokeWidth: 2 }}
                            >
                              {selectedParticipant.layout.total_area} m²
                            </text>
                            {/* Tenant country */}
                            <text
                              x={padding + (w * cellSize) / 2}
                              y={20}
                              textAnchor="middle"
                              fill="#1e3a8a"
                              fontSize="13"
                              fontWeight="700"
                            >
                              {(enrichedDetail && enrichedDetail.country) || selectedParticipant.country}
                            </text>

                            {/* Dynamic edge lines (open vs closed segments) */}
                            {cells.length > 0 && (
                              <>
                                {getEdgeSegments(cells, w, h, 'north').map((seg, i) => (
                                  <line 
                                    key={`n-${i}`}
                                    x1={50 + seg.x1 * cellSize} 
                                    y1={50 + seg.y1 * cellSize} 
                                    x2={50 + seg.x2 * cellSize} 
                                    y2={50 + seg.y2 * cellSize}
                                    stroke={getSegmentColor(cells, w, h, selectedParticipant.layout.open_sides, 'north', seg.x1, seg.y1)}
                                    strokeWidth="8"
                                    strokeLinecap="square"
                                  />
                                ))}
                                
                                {getEdgeSegments(cells, w, h, 'south').map((seg, i) => (
                                  <line 
                                    key={`s-${i}`}
                                    x1={50 + seg.x1 * cellSize} 
                                    y1={50 + seg.y1 * cellSize} 
                                    x2={50 + seg.x2 * cellSize} 
                                    y2={50 + seg.y2 * cellSize}
                                    stroke={getSegmentColor(cells, w, h, selectedParticipant.layout.open_sides, 'south', seg.x1, seg.y1)}
                                    strokeWidth="8"
                                    strokeLinecap="square"
                                  />
                                ))}
                                
                                {getEdgeSegments(cells, w, h, 'east').map((seg, i) => (
                                  <line 
                                    key={`e-${i}`}
                                    x1={50 + seg.x1 * cellSize} 
                                    y1={50 + seg.y1 * cellSize} 
                                    x2={50 + seg.x2 * cellSize} 
                                    y2={50 + seg.y2 * cellSize}
                                    stroke={getSegmentColor(cells, w, h, selectedParticipant.layout.open_sides, 'east', seg.x1, seg.y1)}
                                    strokeWidth="8"
                                    strokeLinecap="square"
                                  />
                                ))}
                                
                                {getEdgeSegments(cells, w, h, 'west').map((seg, i) => (
                                  <line 
                                    key={`w-${i}`}
                                    x1={50 + seg.x1 * cellSize} 
                                    y1={50 + seg.y1 * cellSize} 
                                    x2={50 + seg.x2 * cellSize} 
                                    y2={50 + seg.y2 * cellSize}
                                    stroke={getSegmentColor(cells, w, h, selectedParticipant.layout.open_sides, 'west', seg.x1, seg.y1)}
                                    strokeWidth="8"
                                    strokeLinecap="square"
                                  />
                                ))}
                              </>
                            )}
                            
                            {/* A,B,C,D segment labels — same colors as Hall Management open_segments */}
                            <g><circle cx={50 + w * cellSize / 2} cy={25} r="18" fill="#ef4444" /><text x={50 + w * cellSize / 2} y={31} textAnchor="middle" fontSize="16" fontWeight="bold" fill="white">A</text></g>
                            <g><circle cx={50 + w * cellSize + 25} cy={50 + h * cellSize / 2} r="18" fill="#f59e0b" /><text x={50 + w * cellSize + 25} y={50 + h * cellSize / 2 + 6} textAnchor="middle" fontSize="16" fontWeight="bold" fill="white">B</text></g>
                            <g><circle cx={50 + w * cellSize / 2} cy={50 + h * cellSize + 25} r="18" fill="#8b5cf6" /><text x={50 + w * cellSize / 2} y={50 + h * cellSize + 31} textAnchor="middle" fontSize="16" fontWeight="bold" fill="white">C</text></g>
                            <g><circle cx={25} cy={50 + h * cellSize / 2} r="18" fill="#10b981" /><text x={25} y={50 + h * cellSize / 2 + 6} textAnchor="middle" fontSize="16" fontWeight="bold" fill="white">D</text></g>
                          </svg>
                        );
                      })()}
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center font-bold">A</div>
                        <span>{language === 'tr' ? 'A Kenarı' : 'Side A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">B</div>
                        <span>{language === 'tr' ? 'B Kenarı' : 'Side B'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold">C</div>
                        <span>{language === 'tr' ? 'C Kenarı' : 'Side C'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">D</div>
                        <span>{language === 'tr' ? 'D Kenarı' : 'Side D'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-1 bg-green-500 rounded"></div>
                        <span>{language === 'tr' ? 'Açık Kenar' : 'Open Side'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-1 bg-red-500 rounded"></div>
                        <span>{language === 'tr' ? 'Kapalı Kenar' : 'Closed Side'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="border-0 shadow-2xl rounded-3xl max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-xl text-red-600">{language === 'tr' ? 'Silme Onay\u0131' : 'Delete Confirmation'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                {language === 'tr' ? 'Bu katılımcıyı silmek için Ultra Password giriniz:' : 'Enter Ultra Password to delete this participant:'}
              </p>
              <div>
                <Label htmlFor="deletePass">{language === 'tr' ? 'Ultra Password' : 'Ultra Password'}</Label>
                <div className="relative mt-1">
                  <Input
                    id="deletePass"
                    type={deleteShowPw ? 'text' : 'password'}
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="rounded-xl pr-10"
                    placeholder="••••••"
                  />
                  <button type="button" onClick={() => setDeleteShowPw(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors">
                    {deleteShowPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {deleteError && <p className="text-red-600 text-sm mt-2">{deleteError}</p>}
              </div>
              <div className="flex gap-3">
                <Button onClick={() => { setDeleteId(null); setDeletePassword(''); setDeleteError(''); }} variant="outline" className="flex-1 rounded-full">
                  {t('cancel')}
                </Button>
                <Button onClick={handleDeleteConfirm} className="flex-1 bg-red-600 hover:bg-red-700 rounded-full">
                  {language === 'tr' ? 'Sil' : 'Delete'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BlueZoneLayouts;
